"use client";

import React, { useState, useEffect, useMemo } from "react";
import Link from "next/link";
import { Card, CardContent } from "@/components/ui/card";
import { brandNameToSlug } from "@/lib/utils";
import Image from "next/image";
import { VehicleGroup, ProductGroup, DisplayProduct } from "@/types";
import { ArrowUpRight, Search } from "lucide-react";
import Container from "@/components/ui/container";
import { useSearchParams, usePathname } from "next/navigation";
import { pusherClient } from "@/lib/pusher";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { useRouter } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { ParentWithVariants } from "@/types/products/product-parent";
import { Checkbox } from "@/components/ui/checkbox";
import { useSession } from "next-auth/react";
import ProductCard from "@/components/products/product-card";

interface ProductsContentProps {
  brand: {
    id: string;
    name: string;
    description: string | null;
    logo: string | null;
  };
  isRockBoard: boolean;
  vehicleGroups: VehicleGroup[];
  productGroups: ProductGroup[];
  initialSearchParams: {
    vehicle?: string;
    type?: "custom" | "modular";
    group?: string;
  };
  products: DisplayProduct[];
  initialProductParents: ParentWithVariants[];
}

interface AttributeGroup {
  attributeName: string;
  attributeValue: string;
  variants: DisplayProduct[];
}

interface VariantGroup {
  parent: ParentWithVariants;
  variants: DisplayProduct[];
  attributeGroups: AttributeGroup[];
}

interface GroupedVariants {
  groups: VariantGroup[];
  standaloneProducts: DisplayProduct[];
}

function ParentProductCard({
  parent,
  brandName,
  index,
}: {
  parent: ParentWithVariants;
  brandName: string;
  index: number;
}) {
  const [isHovered, setIsHovered] = useState(false);

  // Find the primary variant if primary_id is set
  const primaryVariant = parent.primary_id
    ? parent.variants.find((variant) => variant.id === parent.primary_id)
    : null;

  // Use primary variant if found, otherwise fall back to first variant
  const targetVariant = primaryVariant || parent.variants[0];

  // Get the part number for the link, or use # as fallback
  const partNumber = targetVariant?.part_number.toLowerCase() || "#";

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      transition={{
        type: "spring",
        stiffness: 250,
        damping: 35,
        delay: index * 0.1,
      }}
    >
      <Link
        href={`/${brandNameToSlug(brandName)}/products/${partNumber}`}
        className='block h-full'
      >
        <Card
          className='h-full border border-border/50 p-4 shadow-none overflow-hidden hover:bg-accent/50 hover:border-muted transition-colors rounded-2xl relative group'
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* Parent Image */}
          <div className='aspect-square relative rounded-2xl overflow-hidden mb-6 bg-muted/50'>
            <Image
              src={
                isHovered && parent.primary_hover_image
                  ? parent.primary_hover_image
                  : parent.primary_images[0] ||
                    parent.parent_image ||
                    "/placeholder-product.png"
              }
              alt={parent.parent_name}
              fill
              className={cn(
                "object-cover transition-all duration-300",
                isHovered && "scale-110"
              )}
              sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
            />
            <div className='absolute top-4 right-4 bg-background/80 backdrop-blur-sm px-3 py-1 rounded-full'>
              <span className='text-sm font-medium'>
                {parent.variants.length}{" "}
                {parent.variants.length === 1 ? "variant" : "variants"}
              </span>
            </div>
          </div>

          {/* Product Info */}
          <CardContent className='px-1 pb-0'>
            <div className='flex-1 min-h-[5rem] mb-2'>
              <h2 className='text-2xl font-bold mb-2'>{parent.parent_name}</h2>
            </div>
            <div className='flex justify-between items-center gap-4 mb-1'>
              <div className='border border-border text-foreground px-3 py-1.5 rounded-full'>
                <span className='font-medium'>
                  {parent.min_price === parent.max_price
                    ? `A$${parent.min_price.toFixed(2)}`
                    : `A$${parent.min_price.toFixed(2)} - A$${parent.max_price.toFixed(2)}`}
                  <span className='text-xs text-muted-foreground'>
                    {" "}
                    excl. GST
                  </span>
                </span>
              </div>
            </div>
          </CardContent>

          <div className='absolute bottom-0 right-0 w-20 h-20 transform translate-x-2 translate-y-2 transition-all duration-300 group-hover:scale-[1.4] origin-bottom-right'>
            <svg viewBox='0 0 1080 1080' className='w-full h-full'>
              <path
                className='fill-primary'
                d='M1080,1080H0L278.42,213.12C319.2,86.13,437.32,0,570.7,0h509.3v1080Z'
              />
            </svg>
            <ArrowUpRight className='w-7 h-7 text-primary-foreground absolute top-1/2 left-1/2 -translate-x-1/3 -translate-y-1/2 transition-transform group-hover:scale-125' />
          </div>
        </Card>
      </Link>
    </motion.div>
  );
}

export default function ProductsContent({
  brand,
  isRockBoard,
  vehicleGroups,
  productGroups,
  initialSearchParams,
  products: initialProducts,
  initialProductParents,
}: ProductsContentProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [products, setProducts] = useState<DisplayProduct[]>(initialProducts);
  const [searchQuery, setSearchQuery] = useState("");
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const [productParents, setProductParents] = useState<ParentWithVariants[]>(
    initialProductParents
  );
  const [showAllVariants, setShowAllVariants] = useState(false);
  const { data: session } = useSession();

  const currentVehicle =
    searchParams.get("vehicle") || initialSearchParams.vehicle;
  const currentType =
    (searchParams.get("type") as "custom" | "modular" | undefined) ||
    initialSearchParams.type;
  const currentGroup = searchParams.get("group") || initialSearchParams.group;

  // Update products when initialProducts change or during navigation
  useEffect(() => {
    setProducts(initialProducts);
  }, [initialProducts]);

  // Update product parents when initialProductParents change
  useEffect(() => {
    setProductParents(initialProductParents);
  }, [initialProductParents]);

  // Set initial load to false after mount
  useEffect(() => {
    setIsInitialLoad(false);
  }, []);

  // Handle navigation changes
  useEffect(() => {
    const handleNavigation = async () => {
      setIsLoading(true);
      try {
        await router.refresh();
      } finally {
        setIsLoading(false);
      }
    };

    handleNavigation();
  }, [pathname, searchParams, router]);

  // Handle URL updates for filters
  const updateFilters = async (params: {
    vehicle?: string;
    type?: string;
    group?: string;
  }) => {
    setIsLoading(true);
    try {
      const newParams = new URLSearchParams(searchParams.toString());

      Object.entries(params).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });

      // Update URL without adding to history
      await router.replace(
        `/${brandNameToSlug(brand.name)}/products${
          newParams.toString() ? `?${newParams.toString()}` : ""
        }`,
        { scroll: false }
      );

      // Force a server rerender to get new products
      router.refresh();
    } catch (error) {
      console.error("Error updating filters:", error);
      setIsLoading(false);
    }
  };

  // Handle search filtering
  const filteredProducts = useMemo(() => {
    if (!products || products.length === 0) return [];
    return products.filter((product) => {
      if (!searchQuery) return true;
      const searchString =
        `${product.title} ${product.part_number}`.toLowerCase();
      return searchString.includes(searchQuery.toLowerCase());
    });
  }, [products, searchQuery]);

  // Product skeleton for loading state
  const ProductSkeleton = () => (
    <div className='animate-pulse h-full'>
      <Card className='h-full border-0 p-4 shadow-none overflow-hidden'>
        {/* Image Skeleton */}
        <div className='aspect-square relative rounded-2xl overflow-hidden mb-6 bg-accent/50'>
          {/* Part Number Badge */}
          <div className='absolute top-4 right-4 w-24 h-7 bg-background/80 backdrop-blur-sm rounded-full' />
        </div>

        {/* Content Skeleton */}
        <CardContent className='px-1'>
          <div className='flex justify-between items-start gap-4'>
            <div className='flex-1 min-h-[5rem]'>
              {/* Title Skeleton - Multiple lines */}
              <div className='space-y-2 mb-2'>
                <div className='h-8 bg-accent/50 rounded w-full' />
                <div className='h-8 bg-accent/50 rounded w-2/3' />
              </div>

              {/* Attribute Badges Skeleton */}
              <div className='flex flex-wrap gap-2 mb-2'>
                <div className='h-6 bg-accent/50 rounded-full w-20' />
                <div className='h-6 bg-accent/50 rounded-full w-24' />
                <div className='h-6 bg-accent/50 rounded-full w-16' />
              </div>

              {/* Price Badge Skeleton */}
              <div className='flex items-baseline gap-2'>
                <div className='bg-accent/50 rounded-full px-4 py-2 flex items-center gap-2'>
                  <div className='h-6 w-24 bg-accent/50 rounded' />
                </div>
              </div>
            </div>
            {/* Arrow Button Skeleton */}
            <div className='bg-accent/50 rounded-full w-12 h-12' />
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Subscribe to Pusher events for real-time updates
  useEffect(() => {
    const channel = pusherClient.subscribe("products");

    // Handle new product creation
    channel.bind("product-created", (newProduct: DisplayProduct) => {
      if (newProduct.brand === brand.id) {
        setProducts((prev) => {
          // Check if product already exists to prevent duplicates
          if (prev.some((product) => product.id === newProduct.id)) {
            return prev;
          }
          return [...prev, newProduct];
        });
      }
    });

    // Handle product updates
    channel.bind("product-updated", (updatedProduct: DisplayProduct) => {
      if (updatedProduct.brand === brand.id) {
        setProducts((prev) =>
          prev.map((product) =>
            product.id === updatedProduct.id ? updatedProduct : product
          )
        );
      }
    });

    // Handle product deletions
    channel.bind("products-deleted", ({ ids }: { ids: string[] }) => {
      setProducts((prev) =>
        prev.filter((product) => !ids.includes(product.id!))
      );
    });

    return () => {
      channel.unbind_all();
      channel.unsubscribe();
    };
  }, [brand.id]);

  // Get product parents for the current filtered view
  const visibleParents = useMemo(() => {
    if (showAllVariants) {
      return [];
    }

    if (!searchQuery && !currentVehicle && !currentType && !currentGroup) {
      // Sort parents by newest to oldest
      return [...productParents].sort(
        (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
      );
    }

    // Filter parents to only include those with variants that match the current filters and sort by newest to oldest
    return productParents
      .filter((parent) => {
        const variantIds = new Set(parent.variants.map((v) => v.id));
        return filteredProducts.some((product) => variantIds.has(product.id));
      })
      .sort(
        (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
      );
  }, [
    productParents,
    filteredProducts,
    searchQuery,
    currentVehicle,
    currentType,
    currentGroup,
    showAllVariants,
  ]);

  // Filter products to exclude variants if showing parents
  const visibleProducts = useMemo(() => {
    if (showAllVariants || !productParents.length) {
      // Sort products by newest to oldest
      return [...filteredProducts].sort(
        (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
      );
    }

    // Get all variant IDs that belong to parents we're currently showing
    const visibleParentVariantIds = new Set(
      visibleParents.flatMap((parent) => parent.variants.map((v) => v.id))
    );

    // Show only products that are not variants of any visible parent and sort by newest to oldest
    return filteredProducts
      .filter((product) => !visibleParentVariantIds.has(product.id))
      .sort(
        (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
      );
  }, [filteredProducts, productParents, visibleParents, showAllVariants]);

  // Create grouped data structure when showing all variants
  const groupedVariants = useMemo((): GroupedVariants => {
    if (!showAllVariants || !productParents.length) {
      return { groups: [], standaloneProducts: [] };
    }

    // Get all variant IDs that belong to parents
    const allParentVariantIds = new Set(
      productParents.flatMap((parent) => parent.variants.map((v) => v.id))
    );

    // Helper function to find the primary grouping attribute for variants
    const findPrimaryAttribute = (
      variants: DisplayProduct[]
    ): string | null => {
      const attributeFrequency: { [key: string]: number } = {};

      variants.forEach((variant) => {
        variant.attribute_values.forEach((attr) => {
          attributeFrequency[attr.label] =
            (attributeFrequency[attr.label] || 0) + 1;
        });
      });

      // Find the attribute that appears most frequently across variants
      // Prioritize common grouping attributes like Color, Size, Material, etc.
      const priorityAttributes = [
        "Color",
        "Colour",
        "Size",
        "Material",
        "Type",
        "Style",
      ];

      for (const priority of priorityAttributes) {
        if (attributeFrequency[priority] && attributeFrequency[priority] > 1) {
          return priority;
        }
      }

      // If no priority attribute found, use the most frequent one
      const mostFrequent = Object.entries(attributeFrequency)
        .filter(([, count]) => count > 1)
        .sort(([, a], [, b]) => b - a)[0];

      return mostFrequent ? mostFrequent[0] : null;
    };

    // Helper function to group variants by attribute
    const groupVariantsByAttribute = (
      variants: DisplayProduct[],
      attributeName: string
    ): AttributeGroup[] => {
      const groups: { [key: string]: DisplayProduct[] } = {};
      const ungrouped: DisplayProduct[] = [];

      variants.forEach((variant) => {
        const attribute = variant.attribute_values.find(
          (attr) => attr.label === attributeName
        );
        if (attribute) {
          const key = attribute.value;
          if (!groups[key]) groups[key] = [];
          groups[key].push(variant);
        } else {
          ungrouped.push(variant);
        }
      });

      const attributeGroups: AttributeGroup[] = Object.entries(groups)
        .sort(([a], [b]) => a.localeCompare(b)) // Sort attribute values alphabetically
        .map(([value, groupVariants]) => ({
          attributeName,
          attributeValue: value,
          variants: groupVariants.sort((a, b) => {
            // Sort by part number within each attribute group
            return a.part_number.localeCompare(b.part_number);
          }),
        }));

      // Add ungrouped variants at the end
      if (ungrouped.length > 0) {
        attributeGroups.push({
          attributeName: "Other",
          attributeValue: "Various",
          variants: ungrouped.sort((a, b) =>
            a.part_number.localeCompare(b.part_number)
          ),
        });
      }

      return attributeGroups;
    };

    // Create groups for each parent that has variants in the filtered products
    const groups = productParents
      .map((parent): VariantGroup | null => {
        const parentVariants = filteredProducts.filter((product) =>
          parent.variants.some((variant) => variant.id === product.id)
        );

        if (parentVariants.length === 0) return null;

        // Sort variants to show primary variant first
        const sortedVariants = [...parentVariants].sort((a, b) => {
          const aIsPrimary = parent.primary_id === a.id;
          const bIsPrimary = parent.primary_id === b.id;

          if (aIsPrimary && !bIsPrimary) return -1;
          if (!aIsPrimary && bIsPrimary) return 1;

          // Secondary sort by part number
          return a.part_number.localeCompare(b.part_number);
        });

        // Find the best attribute to group by
        const primaryAttribute = findPrimaryAttribute(parentVariants);

        // Create attribute groups if we found a suitable attribute
        const attributeGroups = primaryAttribute
          ? groupVariantsByAttribute(parentVariants, primaryAttribute)
          : [];

        return {
          parent,
          variants: sortedVariants,
          attributeGroups,
        };
      })
      .filter((group): group is VariantGroup => group !== null)
      .sort(
        (a, b) =>
          new Date(b.parent.created).getTime() -
          new Date(a.parent.created).getTime()
      );

    // Get standalone products (not belonging to any parent) and sort by newest to oldest
    const standaloneProducts = filteredProducts
      .filter((product) => !allParentVariantIds.has(product.id))
      .sort(
        (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
      );

    return { groups, standaloneProducts };
  }, [showAllVariants, productParents, filteredProducts]);

  return (
    <Container className='my-5 px-4 md:my-10 min-h-[100vh]'>
      {/* Brand Header */}
      <div className='mb-4'>
        <h1 className='text-4xl font-bold mb-2'>
          {brand.name.toLowerCase() === "company" && session?.user?.companyName
            ? `${session.user.companyName} Products`
            : brand.name}
          {brand.name.toLowerCase() !== "company" && (
            <sup className='text-sm relative -top-5 ml-1'>®</sup>
          )}
        </h1>
        {brand.name.toLowerCase() === "company" ? (
          <p className='text-lg text-muted-foreground'>
            Exclusive products made for your company
          </p>
        ) : brand.description ? (
          <p className='text-lg text-muted-foreground'>{brand.description}</p>
        ) : null}
      </div>

      {/* Search and Filters Container */}
      <div className='space-y-4 mb-8'>
        <div className='flex flex-col md:flex-row gap-4'>
          {/* Search Input */}
          <div className='relative flex-1 flex gap-4'>
            <span className='relative flex-1'>
              <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4' />
              <Input
                type='text'
                placeholder='Search products...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className='pl-10 w-full'
                disabled={isLoading}
              />
            </span>
            {/* Type Filter Inline - Only shown for Rock Board */}
            {isRockBoard && (
              <div className='w-40'>
                <Select
                  options={[
                    { label: "All Types", value: "all" },
                    { label: "Custom", value: "custom" },
                    { label: "Modular", value: "modular" },
                  ]}
                  selected={
                    [
                      { label: "All Types", value: "all" },
                      { label: "Custom", value: "custom" },
                      { label: "Modular", value: "modular" },
                    ].find((opt) => opt.value === currentType) || {
                      label: "All Types",
                      value: "all",
                    }
                  }
                  onChange={async (option) => {
                    if (option && !Array.isArray(option)) {
                      await updateFilters({
                        type: option.value === "all" ? undefined : option.value,
                        group: currentGroup,
                      });
                    }
                  }}
                  placeholder='Select Type'
                  disabled={isLoading}
                />
              </div>
            )}
          </div>

          {/* Product Group Filter */}
          {productGroups.length > 0 && (
            <div className='flex-1 md:max-w-xs'>
              <Select
                options={[
                  { label: "All Products", value: "all" },
                  ...productGroups.map((group) => ({
                    label: group.name,
                    value: group.name
                      .toLowerCase()
                      .replace(/\s*&\s*/g, "-")
                      .replace(/\s+/g, "-")
                      .trim(),
                  })),
                ]}
                selected={
                  productGroups.find(
                    (group) =>
                      group.name
                        .toLowerCase()
                        .replace(/\s*&\s*/g, "-")
                        .replace(/\s+/g, "-")
                        .trim() === currentGroup
                  )
                    ? {
                        label:
                          productGroups.find(
                            (group) =>
                              group.name
                                .toLowerCase()
                                .replace(/\s*&\s*/g, "-")
                                .replace(/\s+/g, "-")
                                .trim() === currentGroup
                          )?.name || "All Products",
                        value: currentGroup || "all",
                      }
                    : { label: "All Products", value: "all" }
                }
                onChange={async (option) => {
                  if (option && !Array.isArray(option)) {
                    await updateFilters({
                      group: option.value === "all" ? undefined : option.value,
                      type: currentType,
                    });
                  }
                }}
                placeholder='Select Product Group'
                disabled={isLoading}
              />
            </div>
          )}
        </div>

        {/* Filters for Rock Board */}
        {isRockBoard && vehicleGroups && (
          <div className='flex flex-col-reverse md:flex-row w-full justify-between gap-4'>
            {/* Results Counter and Variant Toggle */}
            <div className='order-last md:order-first mt-2 md:mt-0 md:py-2 flex flex-col md:flex-row items-start md:items-center gap-2'>
              <p className='text-sm text-muted-foreground'>
                {showAllVariants
                  ? `${groupedVariants.groups.reduce((acc, group) => acc + group.variants.length, 0) + groupedVariants.standaloneProducts.length} ${groupedVariants.groups.reduce((acc, group) => acc + group.variants.length, 0) + groupedVariants.standaloneProducts.length === 1 ? "result" : "results"}`
                  : `${visibleProducts.length + visibleParents.length} ${visibleProducts.length + visibleParents.length === 1 ? "result" : "results"}`}
              </p>

              {/* Show Variants Toggle */}
              {productParents.length > 0 && (
                <div className='flex items-center space-x-2'>
                  <Checkbox
                    id='show-variants'
                    checked={showAllVariants}
                    onCheckedChange={(checked) =>
                      setShowAllVariants(checked === true)
                    }
                  />
                  <label
                    htmlFor='show-variants'
                    className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
                  >
                    Show all variants
                  </label>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Show Variants Toggle for non-Rock Board products */}
        {!isRockBoard && productParents.length > 0 && (
          <div className='flex items-center space-x-2'>
            <p className='text-sm text-muted-foreground mr-4'>
              {showAllVariants
                ? `${groupedVariants.groups.reduce((acc, group) => acc + group.variants.length, 0) + groupedVariants.standaloneProducts.length} ${groupedVariants.groups.reduce((acc, group) => acc + group.variants.length, 0) + groupedVariants.standaloneProducts.length === 1 ? "result" : "results"}`
                : `${visibleProducts.length + visibleParents.length} ${visibleProducts.length + visibleParents.length === 1 ? "result" : "results"}`}
            </p>
            <Checkbox
              id='show-variants'
              checked={showAllVariants}
              onCheckedChange={(checked) =>
                setShowAllVariants(checked === true)
              }
            />
            <label
              htmlFor='show-variants'
              className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
            >
              Show all variants
            </label>
          </div>
        )}

        {/* Products Grid */}
        <motion.div
          className='space-y-8'
          layout='position'
          transition={{
            duration: 0.4,
            type: "spring",
            stiffness: 250,
            damping: 35,
          }}
        >
          <AnimatePresence
            initial={false}
            mode='popLayout'
            presenceAffectsLayout
          >
            {isLoading ? (
              // Show skeletons while loading
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
                {Array.from({ length: 6 }).map((_, index) => (
                  <motion.div
                    key={`skeleton-${index}`}
                    initial={isInitialLoad ? false : { opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{
                      type: "spring",
                      stiffness: 250,
                      damping: 35,
                      delay: index * 0.1,
                    }}
                  >
                    <ProductSkeleton />
                  </motion.div>
                ))}
              </div>
            ) : showAllVariants && groupedVariants.groups.length > 0 ? (
              // Show grouped variants with headers when "show all variants" is enabled
              <div className='space-y-12'>
                {groupedVariants.groups.map(
                  (group: VariantGroup, groupIndex: number) => (
                    <motion.div
                      key={group.parent.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{
                        type: "spring",
                        stiffness: 250,
                        damping: 35,
                        delay: groupIndex * 0.1,
                      }}
                      className='space-y-6'
                    >
                      {/* Group Header */}
                      <div className='flex items-center gap-4'>
                        <div className='flex-1'>
                          <h3 className='text-2xl font-bold text-foreground'>
                            {group.parent.parent_name}
                          </h3>
                          <p className='text-muted-foreground'>
                            {group.variants.length}{" "}
                            {group.variants.length === 1
                              ? "variant"
                              : "variants"}
                            {group.attributeGroups.length > 0 && (
                              <span>
                                {" "}
                                • Grouped by{" "}
                                {group.attributeGroups[0]?.attributeName}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>

                      {/* Variants organized by attributes or simple grid */}
                      {group.attributeGroups.length > 0 ? (
                        <div className='space-y-8'>
                          {group.attributeGroups.map((attributeGroup) => (
                            <div
                              key={`${attributeGroup.attributeName}-${attributeGroup.attributeValue}`}
                              className='space-y-4'
                            >
                              {/* Attribute Sub-header */}
                              <div className='flex items-center gap-3'>
                                <div className='h-px bg-border flex-1' />
                                <div className='px-4 py-2 bg-muted/50 rounded-full'>
                                  <span className='text-sm font-medium text-muted-foreground'>
                                    {attributeGroup.attributeName}:{" "}
                                    {attributeGroup.attributeValue}
                                  </span>
                                  <span className='ml-2 text-xs text-muted-foreground'>
                                    ({attributeGroup.variants.length})
                                  </span>
                                </div>
                                <div className='h-px bg-border flex-1' />
                              </div>

                              {/* Attribute Group Variants Grid */}
                              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
                                {attributeGroup.variants.map(
                                  (
                                    product: DisplayProduct,
                                    variantIndex: number
                                  ) => (
                                    <ProductCard
                                      key={product.id}
                                      product={product}
                                      brandName={brand.name}
                                      index={variantIndex}
                                    />
                                  )
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        // Fallback to simple grid if no attribute grouping
                        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
                          {group.variants.map(
                            (product: DisplayProduct, variantIndex: number) => (
                              <ProductCard
                                key={product.id}
                                product={product}
                                brandName={brand.name}
                                index={variantIndex}
                              />
                            )
                          )}
                        </div>
                      )}

                      {/* Separator at the bottom of the group */}
                      <div className='border-b border-border/50 mt-8' />
                    </motion.div>
                  )
                )}

                {/* Standalone Products */}
                {groupedVariants.standaloneProducts.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{
                      type: "spring",
                      stiffness: 250,
                      damping: 35,
                      delay: groupedVariants.groups.length * 0.1,
                    }}
                    className='space-y-6'
                  >
                    <div className='border-b border-border/50 pb-4'>
                      <h3 className='text-2xl font-bold text-foreground'>
                        Other Products
                      </h3>
                      <p className='text-muted-foreground'>
                        {groupedVariants.standaloneProducts.length}{" "}
                        {groupedVariants.standaloneProducts.length === 1
                          ? "product"
                          : "products"}
                      </p>
                    </div>
                    <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
                      {groupedVariants.standaloneProducts.map(
                        (product: DisplayProduct, index: number) => (
                          <ProductCard
                            key={product.id}
                            product={product}
                            brandName={brand.name}
                            index={index}
                          />
                        )
                      )}
                    </div>
                  </motion.div>
                )}
              </div>
            ) : (
              // Original grid layout for parent products and individual products
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
                {/* Parent Product Cards */}
                {visibleParents.map((parent, index) => (
                  <ParentProductCard
                    key={parent.id}
                    parent={parent}
                    brandName={brand.name}
                    index={index}
                  />
                ))}

                {/* Individual Product Cards */}
                {visibleProducts.length > 0 ? (
                  visibleProducts.map((product, index) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      brandName={brand.name}
                      index={visibleParents.length + index}
                    />
                  ))
                ) : visibleParents.length === 0 ? (
                  // Show empty state only when we have no products and no parents
                  <motion.div
                    className='col-span-full text-center py-12'
                    initial={isInitialLoad ? false : { opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{
                      type: "spring",
                      stiffness: 300,
                      damping: 30,
                    }}
                  >
                    <h2 className='text-2xl font-bold mb-4'>
                      No Products Found
                    </h2>
                    <p className='text-muted-foreground'>
                      {searchQuery
                        ? `No products match your search "${searchQuery}"`
                        : currentGroup
                          ? "No products found in this category."
                          : currentVehicle
                            ? "No products found for this vehicle."
                            : `No products available from ${brand.name} at this time.`}
                    </p>
                  </motion.div>
                ) : null}
              </div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </Container>
  );
}
